<template>
  <div v-if="invoices.length" class="billing-history-table__wrapper">
    <VDataTable extraClass="billing-history-table" hidePlaceholder>
      <template slot="thead">
        <tr>
          <th>Date</th>
          <th>Amount</th>
          <th>Payment Method</th>
          <th>Status</th>
          <th>Invoice <span class="extra"> (PDF) </span></th>
        </tr>
      </template>

      <template slot="tbody">
        <tr v-for="(payment, idx) in invoices" :key="idx">
          <td><DateTimeTooltip :datetime="payment.created" /></td>
          <td><AmountFormat :value="payment.total" /></td>
          <td>
            <div class="payment-method-info">
              <img :src="getPaymentMethodIcon(payment.type)" alt="icon" />
              <span>
                {{
                  getPaymentInfoText(
                    payment.type,
                    payment.last4,
                    payment.bank_name
                  )
                }}</span
              >
            </div>
          </td>
          <td class="capFl">{{ payment.status }}</td>
          <td><DownloadLink :url="payment.invoice_pdf" /></td>
        </tr>
      </template>
    </VDataTable>
  </div>
  <div class="no-invoices" v-else>
    No invoices yet
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

// components
import VDataTable from "@/components/VDataTable";
import TemplatesSendDripModal from "@/components/TemplatesSendDripModal";
import TemplatesSendHubspotModal from "@/components/TemplatesSendHubspotModal";
import VConfirmModal from "@/components/VConfirmModal";
import VTooltip from "@/components/VTooltip";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withToasted from "@/mixins/withToasted";
import withCopy from "@/mixins/withCopy";

import { getFormattedDate, capitalizeFirstLetter } from "@/utils/util";
import pdfIcon from "@/assets/images/download.svg";
import pdfIconActive from "@/assets/images/download-active.svg";
import cardImg from "@/assets/images/billing/Card.svg";
import bankImg from "@/assets/images/billing/Bank.svg";

const DateTimeTooltip = Vue.component("date-time-tooltip", {
  props: ["datetime"],
  components: {
    VTooltip
  },
  template: `<VTooltip  extraClass="in-billing" position="up" :text="getFormattedDate(true)" noPadding>
              {{ getFormattedDate() }}
            </VTooltip>`,

  methods: {
    getFormattedDate(atTime = false) {
      const momentdt = moment.unix(this.datetime);

      return atTime
        ? momentdt.format("HH:mm A")
        : momentdt.format("MMM D, YYYY");
    }
  }
});

const AmountFormat = Vue.component("amount-format", {
  props: ["value"],
  methods: {
    getValuePart(val, decimal = false) {
      const price = val / 100;
      const floorPrice = Math.floor(price);

      return decimal
        ? (price - floorPrice).toFixed(2).substring(2)
        : floorPrice.toLocaleString("EN-us");
    }
  },
  template: `<div class="amount-format__wrapper">
                    <span class="currency">$</span><span>{{getValuePart(value)}}</span>.<span class="decimal">{{getValuePart(value, true)}}</span>
            </div>`
});

const DownloadLink = Vue.component("download-link", {
  props: {
    url: VueTypes.string.isRequired,
    text: VueTypes.string.def("Download")
  },

  data() {
    return {
      hovered: false,
      pdfIcon,
      pdfIconActive
    };
  },

  template: `<a :href="url" target="_blank" class="download-link" @click.prevent="openLink(url)" 
                @mouseover="hovered = true"
                @mouseleave="hovered = false">
                <img :src="image" alt="pdficon"/> <span> Download </span>
             </a>`,
  methods: {
    openLink(url) {
      window.open(url, "_blank");
    }
  },

  computed: {
    image() {
      return this.hovered ? pdfIconActive : pdfIcon;
    }
  }
});

export default Vue.component("BillingHistoryTable", {
  mixins: [withNavigate, withToasted, withCopy],

  components: {
    VDataTable,
    TemplatesSendHubspotModal,
    TemplatesSendDripModal,
    VConfirmModal,
    AmountFormat,
    DownloadLink,
    DateTimeTooltip
  },

  computed: {
    tableFields() {
      return {
        headers: [
          {
            key: "date",
            label: "Date"
          },
          {
            key: "amount",
            label: "Amount"
          },
          {
            key: "status",
            label: "Status"
          },
          {
            key: "invoice_pdf",
            label: "Invoice (PDF)"
          }
        ],

        rows: this.invoices.map(historyItem => ({
          values: [
            {
              key: "date",
              component: DateTimeTooltip,
              props: {
                datetime: historyItem.created
              }
            },
            {
              key: "amount",
              component: AmountFormat,
              props: {
                value: historyItem.amount_due
              }
            },
            {
              key: "status",
              value: capitalizeFirstLetter(historyItem.status)
            },
            {
              key: "invoice_pdf",
              component: DownloadLink,
              props: {
                url: historyItem.invoice_pdf
              }
            }
          ]
        }))
      };
    },

    ...mapGetters("billing", ["invoices", "isLoading"])
  },

  methods: {
    formatDate(date) {
      return getFormattedDate(date);
    },

    getPaymentMethodIcon(type) {
      return type === "card" ? cardImg : bankImg;
    },

    getPaymentInfoText(type, last4, bankName = "Bank") {
      return type === "card"
        ? `****-****-****-${last4}`
        : bankName || "UNKNOWN";
    },

    ...mapActions("billing", ["fetchInvoices", "changeSort"])
  }
});
</script>

<style lang="scss" scoped>
$ns: "billing-history-table";

.#{$ns}__wrapper {
  overflow: auto;

  .capFl {
    text-transform: capitalize;
  }
}
</style>

<style lang="scss">
$ns: "billing-history-table";

table.#{$ns} {
  &__wrapper {
    overflow: auto;
  }

  thead,
  tbody {
    tr {
      td,
      th {
        padding: 0;
        color: $title-text-color;
        font-family: "Lato-Regular", Arial, sans-serif;
        font-size: 15px;
        height: 60px;
        vertical-align: middle;
      }

      td {
        height: 60px;
        text-align: left;

        .payment-method-info {
          display: flex;
          align-items: center;
          img {
            margin-right: 8px;
          }
        }
      }

      th {
        color: $primary-color;
        font-size: 15px;
        font-weight: 700;

        .extra {
          font-size: 12px;
          font-family: "Lato-Light", Arial, sans-serif;
        }
      }
    }
  }

  tbody tr {
    &,
    &:hover {
      background-color: transparent;
    }
  }
}

.in-billing {
  &.v-tooltip__wrapper {
    align-items: flex-start;
  }
}
// AmountFormat styles
.amount-format__wrapper {
  span {
    font-weight: 500;
  }
}

.download-link {
  display: flex;
  align-items: center;
  font-family: "Lato-Light", Arial, sans-serif;
  color: $title-text-color;
  text-decoration: unset;

  img {
    margin-right: 8px;
  }

  &:hover {
    color: $primary-color;
  }
}

.no-invoices {
  font-size: 15px;
}
</style>
